import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { useState, ReactNode, useMemo } from 'react';

import useModalSize from '@/hooks/useModalSize';
import { Size } from '@/types';
import { cn } from '@/utils';

type CommonProps = {
  title?: string | ReactNode;
  className?: string;
  size?: Size;
  showCloseButton?: boolean;
  bottomSheetClassName?: string;
  titleClassName?: string;
  disableBodyScroll?: boolean;
  withBack?: boolean;
};

type ControlledProps = {
  variant: 'controlled';
  isControlledOpen: boolean;
  controlledOnClose: () => void;
  render: () => ReactNode;
};

type Props = CommonProps & ControlledProps;

/**
 * Copia de Modal.
 * La estética del modal de imagen/video (PopupImageVideo) es diferente al del modal estándar.
 * Remuevo props que no son necesarios para este componente.
 * Queda parecido al Modal de "Desktop"
 */
const ModalImageVideo = (props: Props) => {
  const {
    render,
    className,
    size = 'md',
    title,
    showCloseButton = true,
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isOpen, _setIsOpen] = useState(false);

  const modalSizeClass = useModalSize(size);

  const contentClass = cn(
    'duration-75 ease-in-out overflow-hidden relative text-left transform transition-all',
    className,
    modalSizeClass
  );

  const modalIsOpen = useMemo(() => {
    if ('isControlledOpen' in props) {
      return props.isControlledOpen;
    }
    return isOpen;
  }, [isOpen, props]);

  const handleCloseOutside = () => {
    if (showCloseButton === false) {
      return undefined;
    }

    return props.controlledOnClose();
  };

  return (
    <>
      <Dialog
        transition
        as="div"
        className="fixed inset-0 z-40 overflow-y-auto focus:outline-none transition duration-75 ease-in-out data-[closed]:opacity-0"
        open={modalIsOpen}
        onClose={handleCloseOutside}
      >
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-neutral-900 bg-opacity-50 transition duration-75 ease-in-out data-[closed]:opacity-0"
          onClick={() => handleCloseOutside()}
        />
        {/* Full-screen container to center the panel */}
        <div
          className={cn(
            'fixed inset-0 flex items-center justify-center p-4 min-h-screen px-1 text-center md:px-4',
            `max-width-[${modalSizeClass}]`
          )}
        >
          {/* The actual dialog panel  */}
          <DialogPanel
            transition
            className={cn(
              'transition duration-75 ease-in-out data-[closed]:scale-95 scale-100',
              contentClass
            )}
          >
            <div>
              {title && (
                <DialogTitle
                  as="h3"
                  className={cn({
                    'text-gdt-gray lg:text-black font-bold text-base text-center':
                      title,
                  })}
                >
                  {title}
                </DialogTitle>
              )}
              {showCloseButton && (
                <button
                  className={`absolute z-[1] right-2 top-2 text-gdt-gray-4 min-h-10 min-w-10`}
                  data-testid="close-modal"
                  type="button"
                  onClick={handleCloseOutside}
                />
              )}
            </div>
            <div>{render()}</div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

export default ModalImageVideo;
